
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ColorFeedback, EnhancedColorResult } from "./types";

export const submitFeedback = async (
  feedback: ColorFeedback, 
  colorResult: EnhancedColorResult | null
): Promise<void> => {
  try {
    // Enhanced feedback with color information
    const enhancedFeedback = {
      ...feedback,
      notes: feedback.notes || (feedback.sizeIssue ? `Maat probleem: gevraagd ${feedback.correctSize}` : 'Standaard feedback')
    };

    const { error } = await supabase
      .from('ai_learning_feedback')
      .insert({
        analysis_id: enhancedFeedback.analysisId,
        feedback_type: enhancedFeedback.feedbackType,
        suggested_correction: enhancedFeedback.suggestedCorrection,
        actual_base_color: enhancedFeedback.actualBaseColor,
        notes: enhancedFeedback.notes
      });

    if (error) throw error;

    // Enhanced analysis record update
    await supabase
      .from('color_analysis_history')
      .update({ 
        was_correct: enhancedFeedback.feedbackType === 'correct',
        user_feedback: enhancedFeedback.notes
      })
      .eq('id', enhancedFeedback.analysisId);

    // Enhanced learning for color-related feedback
    if (enhancedFeedback.feedbackType === 'incorrect' && colorResult) {
      await updateEnhancedLearnedMappings(
        colorResult.parsedInfo.colorCode || '',
        colorResult.parsedInfo.productName || '',
        enhancedFeedback.actualBaseColor || colorResult.basePrediction.baseColor,
        enhancedFeedback.correctSize || colorResult.parsedInfo.size
      );
    }

    const feedbackMessage = enhancedFeedback.sizeIssue 
      ? "Maat feedback opgeslagen!" 
      : "Kleur feedback opgeslagen!";
    
    toast.success(feedbackMessage, {
      description: "De verbeterde AI zal hiervan leren voor toekomstige analyses"
    });

  } catch (error) {
    console.error("Enhanced feedback error:", error);
    toast.error("Kon feedback niet opslaan");
  }
};

const updateEnhancedLearnedMappings = async (
  colorIdentifier: string, 
  productFamily: string, 
  correctBaseColor: string,
  correctSize?: string
) => {
  try {
    const { data: existing } = await supabase
      .from('color_base_mappings')
      .select('*')
      .eq('color_identifier', colorIdentifier)
      .eq('product_family', productFamily)
      .maybeSingle();

    const enhancedMappingData = {
      base_color: correctBaseColor,
      confidence_score: existing ? Math.min(existing.confidence_score + 0.1, 1.0) : 0.8,
      success_rate: existing ? Math.min(existing.success_rate + 0.1, 1.0) : 0.8,
      learn_count: (existing?.learn_count || 0) + 1
    };

    if (existing) {
      await supabase
        .from('color_base_mappings')
        .update(enhancedMappingData)
        .eq('id', existing.id);
    } else {
      await supabase
        .from('color_base_mappings')
        .insert({
          color_identifier: colorIdentifier,
          product_family: productFamily,
          ...enhancedMappingData
        });
    }

    console.log('✅ Enhanced learned mappings updated with color feedback');
  } catch (error) {
    console.error("Enhanced update mappings error:", error);
  }
};
